import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT, CommonModule } from '@angular/common';

@Component({
  selector: 'app-autonomous-machine-revolution',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './autonomous-machine-revolution.component.html',
  styleUrl: './autonomous-machine-revolution.component.scss'
})
export class AutonomousMachineRevolutionComponent implements OnInit, AfterViewInit {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  private jsonLdScript: any;

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 797;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  injectSchemaMarkup(): void {
    const autonomousBlogSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/autonomous-machine-revolution",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/autonomous-machine-revolution",
          "name": "The Autonomous Machine Revolution: Machine Agency, the Machine-to-Machine Economy, and the Future of Work",
          "description": "Explore how autonomous machines, the AI-driven future of work, IoT, and the machine-to-machine economy are reshaping industries and governance.",
          "inLanguage": "en-US",
          "datePublished": "2026-03-12",
          "dateModified": "2026-03-12",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".padding-headningmain",
              ".softwaredev-heading",
              ".section-title"
            ]
          },
          "relatedLink": ["https://www.kalpitatechnologies.com/kalpita-white-papers/autonomous-machine-revolution"]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/autonomous-machine-revolution#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.kalpitatechnologies.com/tech-blogs/blog" },
            { "@type": "ListItem", "position": 3, "name": "The Autonomous Machine Revolution", "item": "https://www.kalpitatechnologies.com/tech-blogs/autonomous-machine-revolution" }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/autonomous-machine-revolution#article",
          "headline": "The Autonomous Machine Revolution: Machine Agency and the Future of Work",
          "articleBody": "The Autonomous Machine Revolution marks a shift from traditional automation to machine agency... Enabled by AI, IoT, robotics, and blockchain, autonomous machines are transforming agriculture, manufacturing, and energy...",
          "keywords": ["autonomous machines", "machine agency", "M2M economy", "economic autonomy", "robot-as-a-service"],
          "author": { "@type": "Person", "name": "Kalpita Technologies Editorial Team" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/autonomous-machine-revolution/autonomous-machine-revolution-banner.png",
          "datePublished": "2026-03-12"
        },
        {
          "@type": "HowTo",
          "name": "How to Implement Trustworthy Autonomous Machines in the Enterprise",
          "description": "A governance-first approach to deploying autonomous systems responsibly.",
          "step": [
            { "@type": "HowToStep", "name": "Define Intent", "text": "Specify high-level business goals and ethical boundaries before deployment." },
            { "@type": "HowToStep", "name": "Foundational Tech", "text": "Integrate AI/ML models, IoT sensors, and edge compute layers." },
            { "@type": "HowToStep", "name": "Controlled Pilot", "text": "Validate machine agency in a sandboxed setting with full observability." },
            { "@type": "HowToStep", "name": "Apply Governance", "text": "Embed explainable AI tooling and human-in-the-loop checkpoints." },
            { "@type": "HowToStep", "name": "Scalability", "text": "Roll out across operations with continuous oversight and retraining loops." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How is autonomy different from automation?",
              "acceptedAnswer": { "@type": "Answer", "text": "Automation executes predefined instructions; autonomy operates on high-level goals and constraints, adapting to environments in real time." }
            },
            {
              "@type": "Question",
              "name": "What is the machine-to-machine (M2M) economy?",
              "acceptedAnswer": { "@type": "Answer", "text": "It is an economic layer where autonomous machines acted as agents that hold digital identities and wallets to trade services like power directly." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "address": [
            {
              "@type": "PostalAddress",
              "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
              "addressLocality": "Austin",
              "addressRegion": "TX",
              "postalCode": "78746",
              "addressCountry": "US"
            }
          ]
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(autonomousBlogSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
